import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import { faGoogle as faGoogleBrand, faGithub as faGithubBrand } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import AuthLayout from '../components/AuthLayout';
import SEO from '../components/SEO';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoginEnabled, setSocialLoginEnabled] = useState(true);
  const [agreeToTerms, setAgreeToTerms] = useState(true);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getApiBaseUrl = () => {
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
    return 'http://localhost:5000/api';
  };
  const API_BASE_URL = getApiBaseUrl();
  const examId = (location.state as { examId?: string })?.examId || new URLSearchParams(location.search).get('examId');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        const socialLoginSetting = response.data.settings?.social_login_enabled;
        if (socialLoginSetting !== undefined) setSocialLoginEnabled(socialLoginSetting.value);
      } catch {
        /* default enabled */
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!agreeToTerms) {
      setError(t('auth.mustAgreeToTerms'));
      return;
    }
    setLoading(true);
    try {
      await register(email, password, fullName, agreeToTerms, subscribeToNewsletter);
      navigate(examId ? `/subscription/${examId}` : '/dashboard');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = (provider: 'google' | 'github') => {
    if (examId) localStorage.setItem('pendingExamId', examId);
    window.location.href = `${API_BASE_URL}/auth/${provider}`;
  };

  return (
    <>
      <SEO title={t('auth.seo.registerTitle')} description={t('auth.seo.registerDescription')} noindex />
      <AuthLayout title={t('auth.createAccount')} subtitle={t('auth.registerSubtitle', 'Start practicing for free')}>
        {error && <div className="gp-alert-error" role="alert">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium mb-2" style={{ color: 'var(--gp-ink)' }}>
              {t('auth.fullName')}
            </label>
            <div className="relative">
              <FontAwesomeIcon icon={faUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted" aria-hidden="true" />
              <input type="text" id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} className="input-field pl-10" autoComplete="name" />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: 'var(--gp-ink)' }}>
              {t('auth.email')}
            </label>
            <div className="relative">
              <FontAwesomeIcon icon={faEnvelope} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted" aria-hidden="true" />
              <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input-field pl-10" autoComplete="email" />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: 'var(--gp-ink)' }}>
              {t('auth.password')}
            </label>
            <div className="relative">
              <FontAwesomeIcon icon={faLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted" aria-hidden="true" />
              <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="input-field pl-10" autoComplete="new-password" />
            </div>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="agreeToTerms"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              required
              className="mt-1 h-4 w-4 rounded"
              style={{ accentColor: 'var(--gp-brand)' }}
            />
            <label htmlFor="agreeToTerms" className="text-sm gp-text-muted">
              {t('auth.agreeToTerms')}{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="gp-link" onClick={(e) => e.stopPropagation()}>
                {t('auth.termsOfService')}
              </Link>
            </label>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="subscribeToNewsletter"
              checked={subscribeToNewsletter}
              onChange={(e) => setSubscribeToNewsletter(e.target.checked)}
              className="mt-1 h-4 w-4 rounded"
              style={{ accentColor: 'var(--gp-brand)' }}
            />
            <label htmlFor="subscribeToNewsletter" className="text-sm gp-text-muted">
              {t('auth.subscribeToNewsletter')}
            </label>
          </div>

          <button type="submit" className="w-full gp-btn-accent py-3" disabled={loading || !agreeToTerms}>
            {loading ? t('auth.registering') : t('auth.signUp')}
          </button>
        </form>

        {socialLoginEnabled && (
          <div className="mt-6">
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'var(--gp-border)' }} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 gp-text-muted" style={{ background: 'var(--gp-surface)' }}>{t('auth.or')}</span>
              </div>
            </div>
            <div className="space-y-3">
              <button type="button" onClick={() => handleSocial('google')} className="gp-social-btn">
                <FontAwesomeIcon icon={faGoogleBrand} className="text-lg text-red-500" aria-hidden="true" />
                {t('auth.loginWithGoogle')}
              </button>
              <button type="button" onClick={() => handleSocial('github')} className="gp-social-btn">
                <FontAwesomeIcon icon={faGithubBrand} className="text-lg" aria-hidden="true" />
                {t('auth.loginWithGitHub')}
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-sm gp-text-muted">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/login" state={examId ? { examId } : undefined} className="gp-link">
            {t('auth.login')}
          </Link>
        </p>
      </AuthLayout>
    </>
  );
};

export default Register;
